import React, { memo } from 'react';
import { Button, Flex, Text, ThemedImage, Icon, Spinner, Callout } from 'core/components';
import kentikAiUser from 'app/assets/kentik-ai-user.svg';
import kentikAiUserDark from 'app/assets/kentik-ai-user-dark.svg';
import { inject } from 'mobx-react';
import { TbLayoutSidebar } from 'react-icons/tb';
import {
  MessageItem,
  AgentMessageBubble,
  MessageHeader,
  Timestamp,
  SubAgentContainer,
  ToolCallCard
} from '../KaizenChatSession.styles';
import StepComponent from './StepComponent';
import { getAgentDisplayName } from '../util/agentDisplayNames';

const stepSorter = (a, b) => (a.step_number || 0) - (b.step_number || 0);

const SubAgentStepGroup = memo(({ agentName, steps, onSelectToolResult }) => {
  const isProcessing = steps.some((s) => s.processing_status === 'PROCESSING' || !s.processing_status);
  const stepCount = steps.length;

  const handleOpenTranscript = () => {
    const reasoning = steps
      .map((s) => s.llm_response?.reasoning)
      .filter(Boolean)
      .join('\n\n');
    const toolCalls = steps.flatMap((s) => s.toolCalls || []);

    onSelectToolResult({
      kind: 'subagent-transcript',
      id: `subagent-transcript-steps-${steps[0]?.id || agentName}-${agentName}`,
      agentName,
      steps,
      reasoning,
      toolCalls,
      title: `${getAgentDisplayName(agentName)} details`
    });
  };

  return (
    <SubAgentContainer>
      <ToolCallCard interactive elevation={0} onClick={handleOpenTranscript}>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap="6px">
            {isProcessing ? <Spinner size={16} /> : <Icon icon="tick-circle" color="success" size={14} />}
            <Flex flexDirection="column" gap="2px">
              <Text fontWeight={500}>{getAgentDisplayName(agentName)}</Text>
              <Text small muted>
                {isProcessing
                  ? `Working... (${stepCount} step${stepCount !== 1 ? 's' : ''})`
                  : `${stepCount} step${stepCount !== 1 ? 's' : ''} completed`}
              </Text>
            </Flex>
          </Flex>
          <Flex alignItems="center" gap="6px">
            <Text muted small>
              View details
            </Text>
            <Icon icon={TbLayoutSidebar} size={14} color="muted" />
          </Flex>
        </Flex>
      </ToolCallCard>
    </SubAgentContainer>
  );
});

SubAgentStepGroup.displayName = 'SubAgentStepGroup';

// Memoized agent message component
const AgentMessage = memo(
  ({
    turn,
    stepReasoningOpenState,
    onToggleReasoning,
    onSelectToolResult,
    onSendToExplorer,
    onShowAgentDataInSidebar,
    formatDate,
    isCopilotMode,
    sessionModel,
    isExistingChatLoading,
    $auth
  }) => {
    const sortedSteps = turn.steps?.slice().sort(stepSorter);

    return (
      <MessageItem key={`agent-${turn.id}`}>
        <AgentMessageBubble>
          <MessageHeader>
            <ThemedImage src={kentikAiUser} darkSrc={kentikAiUserDark} width={24} height={24} />
            <Text fontSize={13} fontWeight="bold">
              Kentik AI
            </Text>
            <Timestamp muted fontSize={13} fontWeight="normal">
              {formatDate(turn.created_at)}
            </Timestamp>

            {$auth.isSudoer && (
              <Button
                small
                minimal
                icon="info-sign"
                onClick={() => onShowAgentDataInSidebar(turn, sortedSteps)}
                text="Show Data in Console"
              />
            )}
          </MessageHeader>

          {(() => {
            // Group consecutive sub-agent steps together, render main steps individually
            const groups = [];
            let currentSubAgent = null;
            let currentSubAgentSteps = [];

            sortedSteps?.forEach((step) => {
              if (step.is_sub_agent_step && step.agent_name) {
                if (currentSubAgent === step.agent_name) {
                  currentSubAgentSteps.push(step);
                } else {
                  if (currentSubAgent) {
                    groups.push({ type: 'subagent', agentName: currentSubAgent, steps: currentSubAgentSteps });
                  }
                  currentSubAgent = step.agent_name;
                  currentSubAgentSteps = [step];
                }
              } else {
                if (currentSubAgent) {
                  groups.push({ type: 'subagent', agentName: currentSubAgent, steps: currentSubAgentSteps });
                  currentSubAgent = null;
                  currentSubAgentSteps = [];
                }
                groups.push({ type: 'main', step });
              }
            });

            if (currentSubAgent) {
              groups.push({ type: 'subagent', agentName: currentSubAgent, steps: currentSubAgentSteps });
            }

            // Ensure sub-agent groups that trail after the final-answer step are moved before it
            const finalAnswerIdx = groups.findIndex(
              (g) =>
                g.type === 'main' &&
                g.step.processing_status === 'COMPLETED' &&
                g.step.parent_step_id === null &&
                g.step.llm_response?.final_answer
            );
            if (finalAnswerIdx > -1 && finalAnswerIdx < groups.length - 1) {
              const before = groups.slice(0, finalAnswerIdx);
              const finalGroup = groups[finalAnswerIdx];
              const after = groups.slice(finalAnswerIdx + 1);
              groups.length = 0;
              groups.push(...before, ...after, finalGroup);
            }

            return groups.map((group, groupIdx) => {
              if (group.type === 'main') {
                const stepId = group.step.id || groupIdx;
                const isStepReasoningOpen = stepReasoningOpenState[stepId] ?? true;

                return (
                  <StepComponent
                    key={`step-${stepId}`}
                    $auth={$auth}
                    turn={turn}
                    step={group.step}
                    stepIndex={groupIdx}
                    isStepReasoningOpen={isStepReasoningOpen}
                    onToggleReasoning={onToggleReasoning}
                    onSelectToolResult={onSelectToolResult}
                    onSendToExplorer={onSendToExplorer}
                    isCopilotMode={isCopilotMode}
                    isExistingChatLoading={isExistingChatLoading}
                    sessionModel={sessionModel}
                  />
                );
              }

              return (
                <SubAgentStepGroup
                  key={`subagent-steps-${group.agentName}-${group.steps[0]?.id || group.agentName}`}
                  agentName={group.agentName}
                  steps={group.steps}
                  onSelectToolResult={onSelectToolResult}
                />
              );
            });
          })()}

          {turn.status === 'CANCELLED' && (
            <Callout intent="warning" icon="disable">
              <Text small>Response cancelled</Text>
            </Callout>
          )}

          {turn.error_message && (
            <Callout intent="danger">
              <Text small>Error: something went wrong</Text>
            </Callout>
          )}
        </AgentMessageBubble>
      </MessageItem>
    );
  }
);

AgentMessage.displayName = 'AgentMessage';

export default inject('$auth')(AgentMessage);
